import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Select, Stack, Tooltip, Typography,
} from '@mui/material';
import { CheckCircle, ErrorOutline, Face, Upload } from '@mui/icons-material';
import {
  RescheduleBody, RotationClip, RotationItem, RotationState, RotationTimeline, shortsAPI,
  uploadsAPI,
} from '../../services/api';

type Site = 'spoutlore' | 'mallbrats';

// YouTube refuses custom thumbnails over 2 MB; check before uploading.
const MAX_THUMB_BYTES = 2 * 1024 * 1024;

export const STATE_LABEL: Record<RotationState, string> = {
  planned: 'planned',
  live: 'live now',
  settled: 'kept for good',
  swapping: 'swapping…',
  retrying: 'swap failed, retrying',
  failed: 'swap failed',
  shown: 'was live',
  upcoming: 'up next',
  skipped: 'skipped',
  cut_off: 'cut off',
};

export const STATE_COLOR: Record<RotationState,
  'default' | 'info' | 'warning' | 'success' | 'error'> = {
  planned: 'default',
  live: 'success',
  settled: 'success',
  swapping: 'info',
  retrying: 'warning',
  failed: 'error',
  shown: 'default',
  upcoming: 'default',
  skipped: 'default',
  cut_off: 'default',
};

const STATE_HELP: Record<RotationState, string> = {
  planned: 'The Short is not on YouTube yet; this is when it will go up, counted from the slot.',
  live: 'On YouTube right now.',
  settled: 'The rotation has ended and this one stays up.',
  swapping: 'Requested; the swap worker has not confirmed it yet (it checks every 20 minutes).',
  retrying: 'The last swap attempt failed and will be retried. The previous image is still up.',
  failed: 'Every attempt failed, so this one was given up on. The previous image stayed up.',
  shown: 'Had its turn and was replaced by the next one.',
  upcoming: 'Waiting for its slot.',
  skipped: 'The rotation settled before its turn came.',
  cut_off: 'Its slot is past the 8-day test window, so it never goes up.',
};

// Slots count from publish and the rotation never runs past the test window.
export const TEST_WINDOW_DAYS = 8;
const DAY_MS = 86_400_000;
const SETTLE_TEXT: Record<string, string> = {
  best: 'best CTR', first: 'first image', last: 'last image',
};

/** A slot can move while it is still ahead (the server enforces the same). */
export const canMove = (it: RotationItem): boolean =>
  it.after_days != null && it.after_days > 0
  && ['planned', 'upcoming', 'cut_off', 'retrying'].includes(it.state);

/** When a clip's slots count from: YouTube publish, else its planned slot. */
export const rotationStart = (clip: RotationClip): string | null =>
  clip.published_at || clip.scheduled_at;

type Msg = { ok: boolean; text: string };

/** Send a schedule change; the reply's warnings become the message. */
export const sendReschedule = async (key: string, body: RescheduleBody): Promise<Msg> => {
  try {
    const res = await shortsAPI.rescheduleRotation(key, body);
    return res.warnings.length
      ? { ok: true, text: `Saved. Note: ${res.warnings.join('; ')}.` }
      : { ok: true, text: 'Schedule saved.' };
  } catch (e: any) {
    return { ok: false, text: e?.response?.data?.detail || e?.message || String(e) };
  }
};

const fmt = (iso: string | null | undefined) =>
  !iso ? '—' : new Date(iso).toLocaleString(undefined,
    { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** One line saying what happened to this variant, newest event first. */
const whenLine = (it: RotationItem): string => {
  const open = it.live_periods.find((p) => !p.to);
  if (open) return `up since ${fmt(open.from)}`;
  if (it.live_periods.length) {
    const p = it.live_periods[it.live_periods.length - 1];
    return `was up ${fmt(p.from)} → ${fmt(p.to)}`;
  }
  if (it.state === 'swapping') {
    const a = it.attempts[it.attempts.length - 1];
    return `requested ${fmt(a?.requested_at)}`;
  }
  if (it.due_at && ['upcoming', 'planned', 'retrying'].includes(it.state)) {
    return `goes up ${fmt(it.due_at)}`;
  }
  return it.due_at ? `slot ${fmt(it.due_at)}` : 'no slot';
};

const Attempts: React.FC<{ item: RotationItem }> = ({ item }) => {
  if (!item.attempts.length) return null;
  return (
    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
      {item.attempts.map((a, i) => (
        <Stack key={i} direction="row" spacing={0.5} alignItems="flex-start">
          {a.applied_at ? (
            <CheckCircle sx={{ fontSize: 12, color: 'success.main', mt: '2px' }} />
          ) : a.error ? (
            <ErrorOutline sx={{ fontSize: 12, color: 'error.main', mt: '2px' }} />
          ) : (
            <CircularProgress size={10} sx={{ mt: '3px' }} />
          )}
          <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.3 }}
                      color={a.error ? 'error.main' : 'text.secondary'}>
            {a.applied_at
              ? `swapped ${fmt(a.applied_at)}`
              : a.error
                ? `failed ${fmt(a.requested_at)}: ${a.error}`
                : `waiting on the worker since ${fmt(a.requested_at)}`}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

/** Pick a new day for a slot; each option shows its date. */
const SlotSelect: React.FC<{
  item: RotationItem; start: string | null; published: boolean;
  onMove: (day: number) => void;
}> = ({ item, start, published, onMove }) => {
  const now = Date.now();
  const days = Array.from({ length: TEST_WINDOW_DAYS }, (_, i) => i + 1);
  return (
    <Select size="small" variant="standard" value={item.after_days ?? ''}
            onChange={(e) => onMove(Number(e.target.value))}
            renderValue={(v) => `day ${v}`}
            sx={{ fontSize: 11, '& .MuiSelect-select': { py: 0 } }}>
      {days.map((d) => {
        const at = start ? new Date(new Date(start).getTime() + d * DAY_MS) : null;
        const passed = published && at != null && at.getTime() <= now;
        return (
          <MenuItem key={d} value={d} dense>
            day {d}{at ? ` · ${fmt(at.toISOString())}` : ''}
            {passed ? ' (passed: goes up on the next pass)' : ''}
          </MenuItem>
        );
      })}
    </Select>
  );
};

interface CardProps {
  item: RotationItem;
  landscape: boolean;
  busy: boolean;
  start: string | null;
  published: boolean;
  onReplace: (file: File) => void;
  onMove: (day: number) => void;
}

const ThumbCard: React.FC<CardProps> = ({
  item, landscape, busy, start, published, onReplace, onMove,
}) => {
  const input = useRef<HTMLInputElement | null>(null);
  const live = item.state === 'live' || item.state === 'settled';
  return (
    <Box sx={{
      width: landscape ? 200 : 128, flex: '0 0 auto',
      border: '2px solid', borderRadius: 1, p: 0.75,
      borderColor: live ? 'success.main' : item.state === 'failed' ? 'error.main' : 'divider',
      opacity: ['skipped', 'cut_off'].includes(item.state) ? 0.55 : 1,
    }}>
      <Box sx={{
        width: '100%', aspectRatio: landscape ? '16 / 9' : '9 / 16',
        bgcolor: 'action.hover', borderRadius: 0.5, overflow: 'hidden',
      }}>
        {item.image_url ? (
          <Box component="img" src={item.image_url} alt={`rank ${item.rank}`}
               sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Typography variant="caption" sx={{ p: 1, display: 'block' }}>no image</Typography>
        )}
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center"
             flexWrap="wrap" columnGap={0.5} sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          #{item.rank} {item.person || ''}
        </Typography>
        {canMove(item) ? (
          <Tooltip title="Move this slot (days after publish)">
            <span>
              <SlotSelect item={item} start={start} published={published} onMove={onMove} />
            </span>
          </Tooltip>
        ) : item.after_days != null && (
          <Typography variant="caption" color="text.secondary">day {item.after_days}</Typography>
        )}
      </Stack>
      {item.caption && (
        <Typography variant="caption" color="text.secondary" noWrap display="block"
                    title={item.caption} sx={{ fontSize: 10 }}>
          “{item.caption}”
        </Typography>
      )}
      <Tooltip title={STATE_HELP[item.state]}>
        <Chip size="small" color={STATE_COLOR[item.state]}
              variant={live || ['failed', 'retrying', 'swapping'].includes(item.state)
                ? 'filled' : 'outlined'}
              label={STATE_LABEL[item.state]}
              sx={{ height: 18, fontSize: 10, mt: 0.5 }} />
      </Tooltip>
      <Typography variant="caption" display="block" color="text.secondary"
                  sx={{ fontSize: 10, mt: 0.25 }}>
        {whenLine(item)}
      </Typography>
      {(item.slot_edited_at || item.image_replaced_at) && (
        <Typography variant="caption" display="block" color="text.secondary"
                    sx={{ fontSize: 10, fontStyle: 'italic' }}>
          {[item.slot_edited_at && 'slot', item.image_replaced_at && 'image']
            .filter(Boolean).join(' + ')} changed here
        </Typography>
      )}
      <Attempts item={item} />
      <input ref={input} type="file" accept="image/jpeg,image/png" hidden
             onChange={(e) => {
               const f = e.target.files?.[0];
               if (f) onReplace(f);
               e.target.value = '';
             }} />
      <Tooltip title="Upload a different image for this rank. It keeps its slot; if it's live now, YouTube gets the new one on the next worker pass.">
        <span>
          <Button size="small" startIcon={busy ? <CircularProgress size={10} /> : <Upload />}
                  disabled={busy} onClick={() => input.current?.click()}
                  sx={{ mt: 0.5, fontSize: 10, minWidth: 0, p: '2px 4px' }}>
            Replace
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

/** One-line answer to "what's up now and what happens next". */
export const rotationSummary = (t: RotationTimeline, publishedAt: string | null): string => {
  const live = t.items.find((i) => i.state === 'live' || i.state === 'settled');
  const next = t.items.find((i) => i.state === 'upcoming' || i.state === 'swapping'
    || i.state === 'retrying');
  const parts: string[] = [];
  if (!publishedAt) {
    parts.push('not on YouTube yet');
  } else if (live) {
    parts.push(`live: #${live.rank ?? live.idx}${live.person ? ` ${live.person}` : ''}`);
  }
  if (next) {
    parts.push(next.state === 'swapping'
      ? `swapping to #${next.rank ?? next.idx}`
      : `next: #${next.rank ?? next.idx} ${fmt(next.due_at)}`);
  }
  if (t.settled != null) parts.push(`settled on #${t.settled}`);
  else if (t.end_at) parts.push(`settles ${fmt(t.end_at)}`);
  return parts.join(' · ');
};

interface Props {
  clip: RotationClip;
  site: Site;
  landscape?: boolean;
  onChanged: () => void;
  onPickFaces?: () => void;
}

const ShortsRotationPanel: React.FC<Props> = ({
  clip, site, landscape = false, onChanged, onPickFaces,
}) => {
  const [busyRank, setBusyRank] = useState<number | null>(null);
  const [message, setMessage] = useState<Msg | null>(null);
  const [saving, setSaving] = useState(false);
  const t = clip.thumbnails;
  const start = rotationStart(clip);
  const published = !!clip.video_id;
  const settled = t.settled != null;

  const reschedule = async (body: RescheduleBody) => {
    setSaving(true);
    setMessage(await sendReschedule(clip.clip_id, body));
    setSaving(false);
    onChanged();
  };

  const replace = async (rank: number, file: File) => {
    setMessage(null);
    if (!/\.(jpe?g|png)$/i.test(file.name)) {
      setMessage({ ok: false, text: 'Use a .jpg or .png.' });
      return;
    }
    if (file.size > MAX_THUMB_BYTES) {
      setMessage({ ok: false,
        text: `That image is ${(file.size / 1048576).toFixed(1)} MB; YouTube's limit is 2 MB.` });
      return;
    }
    setBusyRank(rank);
    try {
      const staged = await uploadsAPI.uploadToS3Multipart(site, 'short-still', file);
      const res = await shortsAPI.replaceCandidate(clip.clip_id, rank, {
        bucket: staged.bucket, object_key: staged.object_key, filename: file.name,
      });
      setMessage({ ok: true, text: res.repush
        ? `#${rank} replaced. It's live, so the worker puts the new image on YouTube within about 20 minutes.`
        : `#${rank} replaced. It goes up at its slot.` });
      onChanged();
    } catch (e: any) {
      setMessage({ ok: false,
        text: e?.response?.data?.detail || e?.message || String(e) });
    } finally {
      setBusyRank(null);
    }
  };

  return (
    <Stack spacing={1} sx={{ py: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center"
             flexWrap="wrap" gap={1}>
        <Typography variant="caption" color="text.secondary" component="div">
          THUMBNAILS · {rotationSummary(t, clip.published_at)}
          {saving && <CircularProgress size={10} sx={{ ml: 1 }} />}
          {clip.video_id && (
            <> · <a href={`https://studio.youtube.com/video/${clip.video_id}/edit`}
                    target="_blank" rel="noreferrer">open in Studio</a></>
          )}
        </Typography>
        {onPickFaces && (
          <Button size="small" variant="outlined" startIcon={<Face />} onClick={onPickFaces}>
            Pick faces + re-render
          </Button>
        )}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">Ends</Typography>
        <Select size="small" variant="standard" disabled={settled || saving}
                value={clip.end_after_days ?? 'auto'}
                onChange={(e) => reschedule({ end: {
                  after_days: e.target.value === 'auto' ? null : Number(e.target.value),
                  settle: clip.settle } })}
                sx={{ fontSize: 12 }}>
          <MenuItem value="auto" dense>
            automatically (day {t.end_day})
          </MenuItem>
          {Array.from({ length: TEST_WINDOW_DAYS }, (_, i) => i + 1).map((d) => (
            <MenuItem key={d} value={d} dense>on day {d}</MenuItem>
          ))}
        </Select>
        <Typography variant="caption" color="text.secondary">and keeps the</Typography>
        <Select size="small" variant="standard" disabled={settled || saving}
                value={clip.settle}
                onChange={(e) => reschedule({ end: {
                  after_days: clip.end_after_days, settle: String(e.target.value) } })}
                sx={{ fontSize: 12 }}>
          {Object.entries(SETTLE_TEXT).map(([k, label]) => (
            <MenuItem key={k} value={k} dense>{label}</MenuItem>
          ))}
        </Select>
        {settled && (
          <Typography variant="caption" color="text.secondary">
            (settled; the schedule is final)
          </Typography>
        )}
      </Stack>
      {message && (
        <Alert severity={message.ok ? 'success' : 'error'} onClose={() => setMessage(null)}
               sx={{ py: 0 }}>
          {message.text}
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
        {t.items.map((it) => (
          <ThumbCard key={it.rank} item={it} landscape={landscape}
                     busy={busyRank === it.rank} start={start} published={published}
                     onReplace={(f) => replace(it.rank as number, f)}
                     onMove={(day) => reschedule({
                       thumbnails: [{ rank: it.rank as number, after_days: day }] })} />
        ))}
      </Stack>
      {clip.titles && clip.titles.items.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            TITLES · {rotationSummary(clip.titles, clip.published_at)}
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {clip.titles.items.map((it) => (
              <Stack key={it.idx} direction="row" spacing={1} alignItems="center">
                <Tooltip title={STATE_HELP[it.state]}>
                  <Chip size="small" color={STATE_COLOR[it.state]} label={STATE_LABEL[it.state]}
                        sx={{ height: 18, fontSize: 10, minWidth: 90 }} />
                </Tooltip>
                <Typography variant="body2" sx={{ flex: 1 }} noWrap title={it.title}>
                  {it.title}
                </Typography>
                {canMove(it) && (
                  <SlotSelect item={it} start={start} published={published}
                              onMove={(day) => reschedule({
                                titles: [{ idx: it.idx as number, after_days: day }] })} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {whenLine(it)}
                  {it.attempts.some((a) => a.error) &&
                    ` · last error: ${it.attempts.filter((a) => a.error).slice(-1)[0].error}`}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

/** The compact strip for the batch table: live image outlined, failures red. */
export const RotationStrip: React.FC<{ clip: RotationClip; onClick?: () => void }> = ({
  clip, onClick,
}) => (
  <Tooltip title={rotationSummary(clip.thumbnails, clip.published_at)}>
    <Stack direction="row" spacing={0.25} onClick={onClick} sx={{ cursor: 'pointer' }}>
      {clip.thumbnails.items.map((it) => (
        <Box key={it.rank} sx={{
          width: 18, height: 32, borderRadius: 0.5, overflow: 'hidden',
          border: '2px solid',
          borderColor: it.state === 'live' || it.state === 'settled' ? 'success.main'
            : it.state === 'failed' || it.state === 'retrying' ? 'error.main'
              : it.state === 'swapping' ? 'info.main' : 'transparent',
          opacity: ['skipped', 'cut_off', 'shown'].includes(it.state) ? 0.5 : 1,
        }}>
          {it.image_url && (
            <Box component="img" src={it.image_url} alt=""
                 sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          )}
        </Box>
      ))}
    </Stack>
  </Tooltip>
);

export default ShortsRotationPanel;
